/**
 * The "Do" step's list of ready-made jobs (Do v2, Ram 2026-10-24) — the C
 * library catalogue (backend/library_catalogs.py, C_CATALOG) in plain words.
 *
 * Each entry: the C function, its group, what it is called in words, and its
 * parameters, each with its C type and the label the step shows before its
 * chip field ("into", "from"). The label is the catalogue's own label made
 * plain ("Copy String" → "Copy text").
 *
 * The C library is a frontend mirror of the backend catalogue so the list never
 * waits on the network and the tests are deterministic. A test keeps the two in
 * step: every C function in C_CATALOG is here or deliberately left out
 * (LEFT_OUT: screen and keyboard calls belong to the Show and Read steps).
 *
 * A parameter with `words` (its example) takes plain words the step quotes
 * for you — a file name, the text to copy — as well as a text value.
 *
 * Groups, in the order the list shows them:
 *   Text · Characters · Numbers · Files · Memory · Your functions
 */

import { PS_COURSE_SLUG, psModuleNumber } from './moduleFeatures';

export const DO_GROUPS = ['Text', 'Characters', 'Numbers', 'Files', 'Memory', 'Your functions'];

/** In the catalogue but not offered by Do: the Show and Read steps do these. */
export const LEFT_OUT = ['printf', 'scanf', 'puts', 'getchar', 'putchar'];

// [fn, label, [[param, C type, label, extra?]…], returns, extra?]
// extra on an entry: { variadic, keep: false } — keep false: what it gives back
// is only its first argument again (strcpy gives back dest), so Do does not
// offer to keep it (a stored step that keeps it still shows it).
const P = (name, type, label, extra = {}) => ({ name, type, label, ...extra });

const FILE_MODES = [
  ['"r"', 'read'], ['"w"', 'write (start empty)'], ['"a"', 'add to the end'],
  ['"rb"', 'read bytes'], ['"wb"', 'write bytes (start empty)'], ['"ab"', 'add bytes to the end'],
  ['"r+"', 'read and change'],
];
const SEEK_FROM = [['SEEK_SET', 'the start'], ['SEEK_CUR', 'where it is now'], ['SEEK_END', 'the end']];

const TEXT = [
  ['strcpy', 'Copy text', [P('dest', 'char*', 'into'), P('src', 'char*', 'from', { words: 'Ravi' })], 'char*', { keep: false }],
  ['strncpy', 'Copy part of a text', [P('dest', 'char*', 'into'), P('src', 'char*', 'from'), P('n', 'size_t', 'at most')], 'char*', { keep: false }],
  ['strcat', 'Add text to the end', [P('dest', 'char*', 'to'), P('src', 'char*', 'add', { words: ' Kumar' })], 'char*', { keep: false }],
  ['strncat', 'Add part of a text to the end', [P('dest', 'char*', 'to'), P('src', 'char*', 'add'), P('n', 'size_t', 'at most')], 'char*', { keep: false }],
  ['strlen', 'Length of a text', [P('str', 'char*', 'of')], 'size_t'],
  ['strcmp', 'Compare two texts', [P('s1', 'char*', 'compare'), P('s2', 'char*', 'with')], 'int'],
  ['strncmp', 'Compare the start of two texts', [P('s1', 'char*', 'compare'), P('s2', 'char*', 'with'), P('n', 'size_t', 'first')], 'int'],
  ['strchr', 'Find a character in a text', [P('str', 'char*', 'in'), P('c', 'int', 'find')], 'char*'],
  ['strstr', 'Find a text in a text', [P('haystack', 'char*', 'in'), P('needle', 'char*', 'find')], 'char*'],
  ['strpbrk', 'Find any of some characters', [P('str', 'char*', 'in'), P('chars', 'char*', 'any of')], 'char*'],
  ['strtok', 'Split a text into pieces', [P('str', 'char*', 'text'), P('delim', 'char*', 'split at')], 'char*'],
  ['sprintf', 'Write values into a text', [P('buffer', 'char*', 'into'), P('format', 'char*', 'format')], 'int', { variadic: true }],
  ['snprintf', 'Write values into a text, with a limit', [P('buffer', 'char*', 'into'), P('size', 'size_t', 'at most'), P('format', 'char*', 'format')], 'int', { variadic: true }],
  ['sscanf', 'Read values from a text', [P('str', 'char*', 'from'), P('format', 'char*', 'format')], 'int', { variadic: true }],
  ['atoi', 'Whole number written in a text', [P('str', 'char*', 'from', { words: '42' })], 'int'],
  ['atof', 'Decimal written in a text', [P('str', 'char*', 'from', { words: '3.5' })], 'double'],
];

const CHARACTERS = [
  ['isdigit', 'Is it a digit?', [P('c', 'int', 'character')], 'int'],
  ['isalpha', 'Is it a letter?', [P('c', 'int', 'character')], 'int'],
  ['isalnum', 'Is it a letter or a digit?', [P('c', 'int', 'character')], 'int'],
  ['isspace', 'Is it a space?', [P('c', 'int', 'character')], 'int'],
  ['toupper', 'Capital of a letter', [P('c', 'int', 'of')], 'int'],
  ['tolower', 'Small letter of a letter', [P('c', 'int', 'of')], 'int'],
];

const NUMBERS = [
  ['sqrt', 'Square root', [P('x', 'double', 'of')], 'double'],
  ['pow', 'Power', [P('base', 'double', 'number'), P('exp', 'double', 'to the power')], 'double'],
  ['abs', 'Without its sign (whole number)', [P('n', 'int', 'of')], 'int'],
  ['fabs', 'Without its sign (decimal)', [P('x', 'double', 'of')], 'double'],
  ['round', 'Round', [P('x', 'double', 'of')], 'double'],
  ['floor', 'Round down', [P('x', 'double', 'of')], 'double'],
  ['ceil', 'Round up', [P('x', 'double', 'of')], 'double'],
  ['sin', 'Sine', [P('x', 'double', 'of')], 'double'],
  ['cos', 'Cosine', [P('x', 'double', 'of')], 'double'],
  ['tan', 'Tangent', [P('x', 'double', 'of')], 'double'],
  ['log', 'Natural log', [P('x', 'double', 'of')], 'double'],
  ['log10', 'Log to base 10', [P('x', 'double', 'of')], 'double'],
  ['exp', 'e to the power', [P('x', 'double', 'of')], 'double'],
  ['rand', 'A random number', [], 'int'],
  ['srand', 'Start the random numbers', [P('seed', 'unsigned int', 'from')], 'void'],
  ['time', 'The time now', [P('timer', 'time_t*', 'also into')], 'time_t'],
];

const FILES = [
  ['fopen', 'Open a file', [P('path', 'char*', 'name', { words: 'marks.txt' }), P('mode', 'char*', 'how', { choices: FILE_MODES })], 'FILE*'],
  ['fclose', 'Close a file', [P('stream', 'FILE*', 'file')], 'int'],
  ['fprintf', 'Write values to a file', [P('stream', 'FILE*', 'file'), P('format', 'char*', 'format')], 'int', { variadic: true }],
  ['fscanf', 'Read values from a file', [P('stream', 'FILE*', 'file'), P('format', 'char*', 'format')], 'int', { variadic: true }],
  ['fputs', 'Write a text to a file', [P('str', 'char*', 'text', { words: 'Hello' }), P('stream', 'FILE*', 'file')], 'int'],
  ['fputc', 'Write a character to a file', [P('ch', 'int', 'character'), P('stream', 'FILE*', 'file')], 'int'],
  ['fgets', 'Read a line from a file', [P('buffer', 'char*', 'into'), P('n', 'int', 'at most'), P('stream', 'FILE*', 'file')], 'char*'],
  ['fgetc', 'Read a character from a file', [P('stream', 'FILE*', 'file')], 'int'],
  ['fwrite', 'Write a block to a file', [P('ptr', 'void*', 'from'), P('size', 'size_t', 'each of size'), P('count', 'size_t', 'how many'), P('stream', 'FILE*', 'file')], 'size_t'],
  ['fread', 'Read a block from a file', [P('ptr', 'void*', 'into'), P('size', 'size_t', 'each of size'), P('count', 'size_t', 'how many'), P('stream', 'FILE*', 'file')], 'size_t'],
  ['feof', 'At the end of a file?', [P('stream', 'FILE*', 'file')], 'int'],
  ['fseek', 'Move to a place in a file', [P('stream', 'FILE*', 'file'), P('offset', 'long', 'by'), P('origin', 'int', 'from', { choices: SEEK_FROM })], 'int'],
  ['ftell', 'Where we are in a file', [P('stream', 'FILE*', 'file')], 'long'],
  ['rewind', 'Back to the start of a file', [P('stream', 'FILE*', 'file')], 'void'],
  ['remove', 'Delete a file', [P('path', 'char*', 'name', { words: 'old.txt' })], 'int'],
  ['rename', 'Rename a file', [P('oldPath', 'char*', 'name', { words: 'old.txt' }), P('newPath', 'char*', 'new name', { words: 'new.txt' })], 'int'],
];

const MEMORY = [
  ['malloc', 'Get memory', [P('size', 'size_t', 'bytes')], 'void*'],
  ['calloc', 'Get memory, all zero', [P('count', 'size_t', 'how many'), P('size', 'size_t', 'each of size')], 'void*'],
  ['realloc', 'Resize memory', [P('ptr', 'void*', 'memory'), P('size', 'size_t', 'bytes')], 'void*'],
  ['free', 'Give memory back', [P('ptr', 'void*', 'memory')], 'void'],
  ['memcpy', 'Copy memory', [P('dest', 'void*', 'into'), P('src', 'void*', 'from'), P('n', 'size_t', 'bytes')], 'void*', { keep: false }],
  ['memset', 'Fill memory', [P('ptr', 'void*', 'memory'), P('value', 'int', 'with'), P('n', 'size_t', 'bytes')], 'void*', { keep: false }],
];

const entry = (group) => ([fn, label, params, returns, extra = {}]) => ({
  fn, group, label, params, returns,
  variadic: !!extra.variadic,
  // Offer "and keep the result in"? Only when it gives back something worth keeping.
  keep: extra.keep !== false && returns !== 'void',
});

/** The C library, as Do offers it. */
export const C_DO_LIBRARY = [
  ...TEXT.map(entry('Text')),
  ...CHARACTERS.map(entry('Characters')),
  ...NUMBERS.map(entry('Numbers')),
  ...FILES.map(entry('Files')),
  ...MEMORY.map(entry('Memory')),
];

const BY_FN = new Map(C_DO_LIBRARY.map((e) => [e.fn, e]));

/** The library entry for a C function name, or null. */
export const libraryEntry = (fn) => BY_FN.get(String(fn || '')) || null;

/** One of the student's own flows as a Do entry ("Your functions"). */
export function flowEntry(flow, signature) {
  const sig = signature || { params: [], hasReturn: false, returnType: 'void' };
  return {
    fn: flow.name,
    group: 'Your functions',
    label: flow.name,
    params: sig.params.map((p) => ({ name: p.name, type: p.dataType, label: p.name })),
    returns: sig.hasReturn ? sig.returnType : 'void',
    variadic: false,
    keep: !!sig.hasReturn,
    flowId: flow.id,
    own: true,
  };
}

/**
 * The keyboard env's `functions` entries for the library, so a stored call
 * reads (and checks) before the backend catalogue has loaded.
 */
export function libraryEnvFunctions() {
  const out = {};
  C_DO_LIBRARY.forEach((e) => {
    out[e.fn] = {
      params: e.params.map((p) => p.name),
      minParams: e.params.length,
      variadic: e.variadic,
      group: e.group,
      returnsNothing: e.returns === 'void',
      returnType: e.returns,
    };
  });
  return out;
}

// ---- module gating (Ram, 2026-10-24, decision 4) -----------------------------------------
// The Problem Solving course meets each group in its own module: numbers from
// the start, text and characters from ps-m7, memory from ps-m10, files from
// ps-m11. The C Programming course, no course, an unknown module: everything.
// Only the list is gated: a stored step always shows what it does.
const PS_GROUP_FROM = { Numbers: 1, Text: 7, Characters: 7, Memory: 10, Files: 11, 'Your functions': 1 };

/** The groups Do offers for an item's placement ({ courseSlug, moduleSlug }). */
export function doGroupsFor(placement) {
  const { courseSlug, moduleSlug } = placement || {};
  if (courseSlug !== PS_COURSE_SLUG) return [...DO_GROUPS];
  const n = psModuleNumber(moduleSlug);
  if (n == null || n < 1) return [...DO_GROUPS];
  return DO_GROUPS.filter((g) => PS_GROUP_FROM[g] <= n);
}
